import { useState, useEffect, useMemo, useCallback } from 'react';
import { ValidatedOptions } from '@patternfly/react-core';
import { BulkCheckItem } from '../helpers/apiHelpers';
import { useDeployRepoParams } from './useDeployRepoParams';

// Basic regex pattern for valid branch names (letters, numbers, dot, underscore, hyphen, slash)
const branchRegex = /^[a-zA-Z0-9._/-]+$/;

export function useFormState(isRefresh: boolean, businessData: any, isStandalone: boolean) {
  // Form state
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<string[]>([]);
  const [checkboxStates, setCheckboxStates] = useState<Record<string, boolean>>({});
  const [deploymentBranch, setDeploymentBranch] = useState('master');
  const [applicationBranch, setApplicationBranch] = useState('');
  const [namespace, setNamespace] = useState('');
  const [location, setLocation] = useState('USA');
  const [buildRailsEnv, setBuildRailsEnv] = useState('staging');
  const [standalone, setStandalone] = useState(false);
  const [standaloneSelections, setStandaloneSelections] = useState<string[]>([]);
  const [team, setTeam] = useState('Other');
  const [envClass, setEnvClass] = useState('ephemeral');
  const [lastBuiltImage, setLastBuiltImage] = useState(true);
  const [extraEnvVars, setExtraEnvVars] = useState<string[]>([]);
  const [enableNewRelic, setEnableNewRelic] = useState(false);
  const [downmergeMaster, setDownmergeMaster] = useState(true);
  const [enableRegressionProfile, setEnableRegressionProfile] = useState(false);
  const [extraEnvVarsString, setExtraEnvVarsString] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isAdvancedOptionsOpen, setIsAdvancedOptionsOpen] = useState(false);
  const [granularDeployment, setGranularDeployment] = useState<boolean>(false);
  const [skipQuota, setSkipQuota] = useState(false);

  const deployRepoParams = useDeployRepoParams(applicationBranch);

  // Validation state
  const [deploymentBranchValidated, setDeploymentBranchValidated] = useState(ValidatedOptions.success);
  const [applicationBranchValidated, setApplicationBranchValidated] = useState(ValidatedOptions.default);
  const [namespaceValidated, setNamespaceValidated] = useState(ValidatedOptions.default);

  // Hydration state
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    localStorage.removeItem('step2Selections')
  }, [])

  // Refresh payload handling
  const refreshPayload = useMemo(() => {
    if (!isRefresh) return null;
    try { return JSON.parse(localStorage.getItem('refreshPayload') || 'null'); }
    catch { return null; }
  }, [isRefresh]);

  const refreshItems: BulkCheckItem[] = useMemo(() => {
    return isRefresh && refreshPayload?.items ? refreshPayload.items : [];
  }, [isRefresh, refreshPayload]);

  // Generic branch validation function
  const validateBranch = (branch: string) => branchRegex.test(branch);

  // Initialize checkbox states using dependencies from releases that belong to selected experiences
  const initializeCheckboxStates = useCallback((units: string[]) => {
    // standalone: nothing pre-selected
    if (isStandalone) {
      const empty: Record<string, boolean> = {};
      if (businessData) {
        businessData.releases.forEach((r) => { empty[r.name] = false; });
      }
      setCheckboxStates(empty);
      return;
    }

    const newStates: Record<string, boolean> = {};
    // Ensure businessData is available before using it
    if (businessData) {
      businessData.releases.forEach((release) => {
        // Only process releases that match a selected experience
        const experiences = release.part_of_product || [];
        const belongs = experiences.some(exp => units.includes(exp)) || experiences.includes('*');
        if (belongs) {
          newStates[release.name] = true; // Top-level release pre-selected
          release.dependencies.forEach((dep: any) => {
            // Use a unique key (combining release and dependency name) for children
            newStates[`${release.name}_${dep.name}`] = true;
          });
        } else {
          newStates[release.name] = false; // Top-level release not pre-selected
        }
        if (experiences.some((exp: string) => units.includes(exp))) {
          newStates[release.name] = false; // Top-level release not pre-selected
          release.dependencies.forEach((dep: any) => {
            // Use a unique key (combining release and dependency name) for children
            newStates[`${release.name}_${dep.name}`] = false;
          });
        }
      });
    }
    setCheckboxStates(newStates);
  }, [isStandalone, businessData]);

  const handleBusinessUnitSelect = useCallback((unitName: string) => {
    const updatedUnits = selectedBusinessUnits.includes(unitName)
      ? selectedBusinessUnits.filter((unit) => unit !== unitName)
      : [...selectedBusinessUnits, unitName];

    setSelectedBusinessUnits(updatedUnits);
    initializeCheckboxStates(updatedUnits);
    setErrorMessage(null);
  }, [selectedBusinessUnits, initializeCheckboxStates]);

  const handleDeploymentBranchChange = useCallback((value: string) => {
    setDeploymentBranch(value);
    setDeploymentBranchValidated(validateBranch(value) ? ValidatedOptions.success : ValidatedOptions.error);
  }, []);

  const handleApplicationBranchChange = useCallback((value: string) => {
    setApplicationBranch(value);
    setApplicationBranchValidated(validateBranch(value) ? ValidatedOptions.success : ValidatedOptions.error);
  }, []);

  const handleNamespaceChange = useCallback((value: string) => {
    setNamespace(value);
    setNamespaceValidated(branchRegex.test(value) ? ValidatedOptions.success : ValidatedOptions.error);
  }, []);

  const toggleAdvancedOptions = useCallback(() => setIsAdvancedOptionsOpen((prev) => !prev), []);

  // Hydration from localStorage
  useEffect(() => {
    if (hydrated) return;
    //guard not to overwrite values in refresh flow
    if (isRefresh) {
      setHydrated(true);
      return;
    }

    const raw = localStorage.getItem('reviewData');
    if (!raw) {
      setHydrated(true);
      return;
    }
    // Parse the stored data
    const data = JSON.parse(raw);

    // Step 1 state…
    // Feature branch + validation
    if (data.applicationBranch) {
      setApplicationBranch(data.applicationBranch);
      setApplicationBranchValidated(
        validateBranch(data.applicationBranch)
          ? ValidatedOptions.success
          : ValidatedOptions.error
      );
    }

    // Medulla branch + validation
    if (data.deployBranch) {
      setDeploymentBranch(data.deployBranch);
      setDeploymentBranchValidated(
        validateBranch(data.deployBranch)
          ? ValidatedOptions.success
          : ValidatedOptions.error
      );
    }

    if (data.deployRepoBranch) {
      deployRepoParams.onChange(data.deployRepoBranch)
    }

    // Namespace + validation
    if (data.namespace) {
      setNamespace(data.namespace);
      setNamespaceValidated(
        validateBranch(data.namespace)
          ? ValidatedOptions.success
          : ValidatedOptions.error
      );
    }

    if (Array.isArray(data.selectedBusinessUnits)) {
      setSelectedBusinessUnits(data.selectedBusinessUnits);
      initializeCheckboxStates(data.selectedBusinessUnits);
    }

    // generic tree‐mode checkboxes
    if (Array.isArray(data.selectedDependencies)) {
      setCheckboxStates(prev => {
        const next = { ...prev };
        data.selectedDependencies.forEach((d: any) => { next[d.name] = true });
        return next;
      });
    }

    // **Standalone flow hydration:**
    if (data.standalone) {
      setStandalone(true);
      setStandaloneSelections(
        Array.isArray(data.selectedDependencies)
          ? data.selectedDependencies.map((d: any) => d.name)
          : []
      );
    }

    // rest of Step 1…
    if (data.team) setTeam(data.team);
    if (data.location) setLocation(data.location);
    if (data.buildRailsEnv) setBuildRailsEnv(data.buildRailsEnv);
    if (data.envClass) setEnvClass(data.envClass);
    if (typeof data.enableNewRelic === 'boolean') setEnableNewRelic(data.enableNewRelic);
    if (typeof data.enableRegressionProfile === 'boolean') setEnableRegressionProfile(data.enableRegressionProfile);
    if (Array.isArray(data.extraEnvVars)) setExtraEnvVars(data.extraEnvVars);
    if (typeof data.lastBuiltImage === 'boolean') setLastBuiltImage(data.lastBuiltImage);
    if (typeof data.downmergeMaster === 'boolean') setDownmergeMaster(data.downmergeMaster);
    if (typeof data.standalone === 'boolean') setStandalone(data.standalone);

    setHydrated(true);
  }, [
    hydrated,
    isRefresh,
    initializeCheckboxStates,
  ]);

  // Handle refresh payload
  useEffect(() => {
    if (!isRefresh || !refreshPayload) return;
    if (refreshPayload.namespace) setNamespace(refreshPayload.namespace);
    if (refreshPayload.team) setTeam(refreshPayload.team);
    if (refreshPayload.branch) setApplicationBranch(refreshPayload.branch);
  }, [isRefresh, refreshPayload]);

  return {
    // Form state
    selectedBusinessUnits,
    setSelectedBusinessUnits,
    checkboxStates,
    setCheckboxStates,
    deploymentBranch,
    setDeploymentBranch,
    applicationBranch,
    setApplicationBranch,
    namespace,
    setNamespace,
    location,
    setLocation,
    buildRailsEnv,
    setBuildRailsEnv,
    standalone,
    setStandalone,
    standaloneSelections,
    setStandaloneSelections,
    team,
    setTeam,
    envClass,
    setEnvClass,
    lastBuiltImage,
    setLastBuiltImage,
    extraEnvVars,
    setExtraEnvVars,
    enableNewRelic,
    setEnableNewRelic,
    downmergeMaster,
    setDownmergeMaster,
    enableRegressionProfile,
    setEnableRegressionProfile,
    extraEnvVarsString,
    setExtraEnvVarsString,
    errorMessage,
    setErrorMessage,
    isAdvancedOptionsOpen,
    setIsAdvancedOptionsOpen,
    granularDeployment,
    setGranularDeployment,
    deployRepoParams,

    // Validation state
    deploymentBranchValidated,
    setDeploymentBranchValidated,
    applicationBranchValidated,
    setApplicationBranchValidated,
    namespaceValidated,
    setNamespaceValidated,

    // Hydration state
    hydrated,
    setHydrated,

    // Refresh data
    refreshPayload,
    refreshItems,

    // Additional state
    skipQuota,
    setSkipQuota,

    // Handlers
    initializeCheckboxStates,
    handleBusinessUnitSelect,
    handleDeploymentBranchChange,
    handleApplicationBranchChange,
    handleNamespaceChange,
    toggleAdvancedOptions,
  };
}
